import lottie from 'lottie-web';
import { loadFonts } from '@loaders/FontLoader';
import { INGREDIENT_KEYS, GAME_ROUNDS, TUTORIAL_ROUND } from '@utils/roundGenerator';

/**
 * Asset manifest for the Pizza fraction game.
 *
 * - CommonAssets: game-scene bg, conveyor parts, CTA & hint buttons, recipe bar
 * - StartSceneAssets: title screen art + play button hover
 * - PizzaAssets: pizza, tray, and 3 sprites per ingredient (active/grey/placed)
 * - EndSceneAssets: confetti + game-over screen + spotlight/reward atlases
 */
const COMMON_ASSETS = [
  ['game-scene-bg',       'CommonAssets/game-scene-bg.png'],
  ['conveyor-rectangle',  'CommonAssets/conveyor-rectangle.png'],
  ['conveyor-belt-1',     'CommonAssets/conveyor-belt-1.png'],
  ['conveyor-belt-2',     'CommonAssets/conveyor-belt-2.png'],
  ['cta-button',          'CommonAssets/cta-button.png'],
  ['cta-check',           'CommonAssets/cta-check.png'],
  ['cta-next',            'CommonAssets/cta-next.png'],
  ['cta-replay',          'CommonAssets/cta-replay.png'],
  ['hint-button',         'CommonAssets/hint-button.png'],
  ['hint-close',          'CommonAssets/hint-close.png'],
  // Figma node 1860:622 — empty recipe bar (no text, no check button).
  // Per-round text overlay PNGs are loaded by buildRecipeTextAssets().
  // The old CommonAssets/recipe-bar.png is intentionally NOT loaded —
  // it has "1/2 of Mushrooms" + the check button baked in.
  ['recipe-bar-pill',     'CommonAssets/recipe-bar-pill.png'],
  ['check-button',          'CommonAssets/check-button.png'],
  ['check-button-disabled', 'CommonAssets/check-button-disabled.png'],
  ['score-dot-filled',      'CommonAssets/score-dot-filled.png'],
  ['score-pill-blank',      'CommonAssets/score-pill-blank.png'],
  ['grey-overlay',          'CommonAssets/grey-overlay.png'],
];

// Tutorial + all game rounds use the same pattern: empty pill (recipe-bar-pill)
// + per-round text overlay PNG. Tutorial's overlay is exported from Figma
// node 1879:14 ("1/2 of the Mushrooms").
function buildRecipeBarAssets() {
  const out = [
    [`recipe-text-${TUTORIAL_ROUND.id}`, `PizzaAssets/recipe-text-${TUTORIAL_ROUND.id}.png`],
  ];
  GAME_ROUNDS.forEach(r => {
    out.push([`recipe-text-${r.id}`, `PizzaAssets/recipe-text-${r.id}.png`]);
  });
  return out;
}

// Per-round "NEW Order / {Ingredient} Pizza" text PNGs (Figma Frame 49).
function buildOrderTextAssets() {
  const out = [];
  out.push([`order-text-${TUTORIAL_ROUND.id}`, `PizzaAssets/order-text-${TUTORIAL_ROUND.id}.png`]);
  GAME_ROUNDS.forEach(r => {
    out.push([`order-text-${r.id}`, `PizzaAssets/order-text-${r.id}.png`]);
  });
  return out;
}

const START_ASSETS = [
  ['start-scene-bg',     'StartSceneAssets/start-scene-bg.png'],
  ['start-scene-flat',   'StartSceneAssets/start-scene-flat.png'],
  ['play-button-hover',  'StartSceneAssets/play-button-hover.png'],
  ['game-name',          'StartSceneAssets/game-name.png'],
  ['landing-bg-art',     'StartSceneAssets/landing-bg-art.png'],
];

const PIZZA_ASSETS = [
  ['pizza',            'PizzaAssets/pizza.png'],
  ['tray',             'PizzaAssets/tray.png'],
  ['pizza-bottom-plate', 'PizzaAssets/pizza-bottom-plate.png'],
  ['tutorial-text-2',  'PizzaAssets/tutorial-text-2.png'],
  ['tutorial-text-3',  'PizzaAssets/tutorial-text-3.png'],
  ['tutorial-text-4',  'PizzaAssets/tutorial-text-4.png'],
  ['tutorial-pointer', 'PizzaAssets/tutorial-pointer.png'],
];

const END_ASSETS = [
  ['game-over-screen', 'EndSceneAssets/game-over-screen.png'],
  ['end-overlay',      'EndSceneAssets/overlay.png'],
  ['reward-bg',        'EndSceneAssets/reward-bg.png'],
  ['rewards-earned',   'EndSceneAssets/rewards-earned.png'],
  ['spotlight-cone',   'uiAssets/spotlight-cone.png'],
  ['pizza-cutter',     'PizzaAssets/pizza-cutter.png'],
  ['btn-play-again',       'uiAssets/btn-play-again.png'],
  ['btn-play-again-hover', 'uiAssets/btn-play-again-hover.png'],
];

function buildIngredientAssets() {
  // Naming convention after the asset inversion fix:
  //   `*-item`  = colourful actual ingredient (sourced from `{ingredient}-placed.png`
  //                in Figma — the plain "{ingredient}" nodes that look like the real food)
  //   `*-empty` = greyed-out empty-slot silhouette (sourced from `{ingredient}-active.png`
  //                in Figma — the "Group XXXXX" placeholder nodes)
  // The third Figma export (`*-grey.png`) is a near-duplicate of `*-active.png`
  // and is ignored here.
  const out = [];
  INGREDIENT_KEYS.forEach(name => {
    out.push([`ing-${name}-item`,  `PizzaAssets/${name}-placed.png`]);
    out.push([`ing-${name}-empty`, `PizzaAssets/${name}-active.png`]);
  });
  return out;
}

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LoadScene' });
  }

  async preload() {
    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');
    await loadFonts();
  }

  create() {
    this.cameras.main.setBackgroundColor('#FFFFFF');
    this.loader = this.buildLottieLoader();

    this.load.on('loaderror', file => {
      console.warn('[LoadScene] missing asset:', file.key, file.src);
    });

    if (this.loader && this.loader.anim) {
      this.loader.anim.playSegments([0, 25], true);
    }

    // Cache-bust image loads on every page load — dev hot-reload would
    // otherwise keep showing stale PNGs after we swap a file on disk
    // (Phaser's loader doesn't bust HTTP cache on its own).
    const v = `v=${Date.now()}`;
    [
      ...COMMON_ASSETS,
      ...START_ASSETS,
      ...PIZZA_ASSETS,
      ...END_ASSETS,
      ...buildIngredientAssets(),
      ...buildRecipeBarAssets(),
      ...buildOrderTextAssets(),
    ].forEach(([key, file]) => this.load.image(key, `assets/${file}?${v}`));

    this.load.multiatlas(
      'endConfetti',
      'assets/EndSceneAssets/confetti/confettiAnimNew.json',
      'assets/EndSceneAssets/confetti',
    );

    // Correct-answer confetti — atlas from template_3_1. 48 frames named
    // "checkconfetti0.png" .. "checkconfetti47.png".
    this.load.atlas(
      'confetti',
      'assets/CommonAssets/checkConfettiAnim.png',
      'assets/CommonAssets/checkConfettiAnim.json',
    );

    // Pizza top-lid closing — 8-sheet multiatlas, 72 frames named
    // "topLidAnim0.png" .. "topLidAnim71.png". Played forward, no reverse.
    this.load.multiatlas(
      'topLidAnim',
      'assets/PizzaAssets/topLidAnim/topLidAnim.json',
      'assets/PizzaAssets/topLidAnim',
    );

    // Reward-reveal multiatlases (base circle, reward light, glitter orbit).
    // Sourced from truck-loading / buzzer-frizer's polished EndScene flow.
    this.load.multiatlas(
      'baseCircle',
      'assets/anims/rewardAnims/baseCircleAnim.json',
      'assets/anims/rewardAnims',
    );
    this.load.multiatlas(
      'rewardLight',
      'assets/anims/rewardAnims/rewardLightAnim.json',
      'assets/anims/rewardAnims',
    );
    this.load.multiatlas(
      'glitter',
      'assets/anims/rewardAnims/glitterAnim.json',
      'assets/anims/rewardAnims',
    );

    this.load.start();

    this.load.on('complete', () => {
      const moveOn = () => {
        this.teardownLoader();
        this.scene.stop('LoadScene');
        this.scene.start('StartScene');
      };

      if (this.loader && this.loader.anim) {
        this.loader.anim.loop = false;
        this.loader.anim.playSegments([26, 41], true);
        this.loader.anim.addEventListener('complete', moveOn);
        this.time.delayedCall(1500, () => { if (this.loader) moveOn(); });
      } else {
        moveOn();
      }
    });
  }

  buildLottieLoader() {
    if (!this.cache.json.exists('loading-anim')) return null;

    const cx = this.cameras.main.width / 2;
    const cy = this.cameras.main.height / 2;
    const W_BOX = 400;
    const H_BOX = 400;

    const dom = this.add.dom(cx, cy, 'div').setOrigin(0.5).setScale(0.7);
    const node = dom.node;
    node.style.width = `${W_BOX}px`;
    node.style.height = `${H_BOX}px`;
    node.style.marginLeft = `${-W_BOX / 2}px`;
    node.style.marginTop = `${-H_BOX / 2}px`;
    node.style.pointerEvents = 'none';

    const anim = lottie.loadAnimation({
      container: node,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    return { dom, anim };
  }

  teardownLoader() {
    if (!this.loader) return;
    try {
      if (this.loader.anim) { this.loader.anim.stop(); this.loader.anim.destroy(); }
      if (this.loader.dom && this.loader.dom.node) this.loader.dom.node.innerHTML = '';
      if (this.loader.dom) this.loader.dom.destroy();
    } catch (_e) { /* best-effort */ }
    this.loader = null;
  }
}
